import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Users, Briefcase, Check, Calendar as CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import ContactButtons from "@/components/ContactButtons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIME_SLOTS = Array.from({ length: 96 }, (_, i) => {
  const hour24 = Math.floor(i / 4);
  const minute = String((i % 4) * 15).padStart(2, "0");
  const period = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute} ${period}`;
});
const ARRIVAL_SLOTS = TIME_SLOTS;
const BAG_COUNTS = Array.from({ length: 21 }, (_, i) => String(i));
const PASSENGER_COUNTS = Array.from({ length: 7 }, (_, i) => String(i + 1));
const RANGE_3 = Array.from({ length: 4 }, (_, i) => String(i));
const RANGE_4 = Array.from({ length: 5 }, (_, i) => String(i));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const inputCls = "bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm";

export default function QuoteForm({ vehicle: vehicleProp = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(vehicleProp);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({
    pickup_location: "",
    dropoff_location: "",
    pickup_date: "",
    pickup_time: "",
    passengers: 1,
    hours: 1,
    notes: "",
    airline: "",
    flight_number: "",
    arrival_time: "",
    num_bags: "0",
    ski_bags: "0",
    child_seats: "0",
    bike_racks: "0",
    e_bikes: "0",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });

  useEffect(() => {
    if (vehicleProp) { setVehicle(vehicleProp); return; }
    api.get("/vehicles?category=suv").then((res) => setVehicle(res.data?.[0] || null)).catch(() => {});
  }, [vehicleProp]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        contact_name: f.contact_name || user.name || "",
        contact_phone: f.contact_phone || user.phone || "",
        contact_email: f.contact_email || user.email || "",
      }));
    }
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isAirport = form.pickup_location.toLowerCase().includes("airport");
  const emailInvalid = form.contact_email.trim().length > 0 && !EMAIL_RE.test(form.contact_email.trim());

  const handleBook = async (e) => {
    e.preventDefault();
    if (!form.pickup_location || !form.dropoff_location || !form.pickup_date || !form.pickup_time) {
      toast.error("Please fill in all trip details.");
      return;
    }
    if (emailInvalid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (isAirport && (!form.airline || !form.flight_number || !form.arrival_time)) {
      toast.error("Please provide your airline, flight number, and scheduled arrival time for airport pickups.");
      return;
    }
    if (!form.contact_name || !form.contact_phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    if (!vehicle) {
      toast.error("Please wait a moment and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: vehicle.id, ...form,
        passengers: Number(form.passengers), hours: Number(form.hours),
        num_bags: Number(form.num_bags), ski_bags: Number(form.ski_bags),
        child_seats: Number(form.child_seats), bike_racks: Number(form.bike_racks), e_bikes: Number(form.e_bikes),
      };
      if (user) {
        await api.post("/bookings", payload);
        toast.success("Quote request submitted! Our concierge will reply shortly.");
        navigate("/dashboard");
      } else {
        await api.post("/bookings/guest", payload);
        toast.success("Quote request submitted! Our concierge will reply shortly.");
        setConfirmed(true);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass rounded-sm p-8" data-testid="booking-form">
      <h2 className="font-heading text-3xl font-light mb-6">Request your quote</h2>

      {confirmed ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-6 text-center" data-testid="booking-confirmed">
          <Check size={36} className="mx-auto text-emerald-400 mb-3" />
          <h3 className="font-heading text-2xl text-white">Quote requested</h3>
          <p className="text-zinc-300 font-light mt-2 text-sm">
            Thank you, {form.contact_name.split(" ")[0]}. Our concierge will contact you at{" "}
            <span className="text-white">{form.contact_phone}</span> shortly with a tailored quote.
          </p>
          <button onClick={() => setConfirmed(false)} data-testid="quote-another" className="inline-block mt-5 gold-text hover:underline text-sm">
            Submit another request →
          </button>
        </div>
      ) : (
        <form onSubmit={handleBook} className="space-y-4">
          <div>
            <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Pickup Location</Label>
            <Input data-testid="input-pickup-location" className={inputCls} placeholder="e.g. ASE Aspen Airport"
              value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Drop-off Location</Label>
            <Input data-testid="input-dropoff-location" className={inputCls} placeholder="e.g. Stonebridge Condos"
              value={form.dropoff_location} onChange={(e) => update("dropoff_location", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="input-pickup-date" className={`${inputCls} h-10 w-full px-3 flex items-center gap-2 text-left text-sm ${form.pickup_date ? "text-white" : "text-zinc-500"}`}>
                    <CalendarIcon size={15} className="gold-text shrink-0" />
                    {form.pickup_date ? format(new Date(form.pickup_date + "T00:00:00"), "MMM d, yyyy") : "Select date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1a1a1d] border-white/15" align="start">
                  <Calendar
                    mode="single"
                    selected={form.pickup_date ? new Date(form.pickup_date + "T00:00:00") : undefined}
                    onSelect={(d) => d && update("pickup_date", format(d, "yyyy-MM-dd"))}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Time</Label>
              <Select value={form.pickup_time} onValueChange={(v) => update("pickup_time", v)}>
                <SelectTrigger data-testid="input-pickup-time" className={`${inputCls} h-10`}>
                  <span className="flex items-center gap-2">
                    <Clock size={15} className="gold-text" />
                    <SelectValue placeholder="Time" />
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Number of Passengers</Label>
            <Select value={String(form.passengers)} onValueChange={(v) => update("passengers", Number(v))}>
              <SelectTrigger data-testid="input-passengers" className={`${inputCls} h-10`}>
                <span className="flex items-center gap-2">
                  <Users size={15} className="gold-text" />
                  <SelectValue placeholder="1" />
                </span>
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                {PASSENGER_COUNTS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAirport && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Airline <span className="gold-text">*</span></Label>
                  <Input data-testid="input-airline" className={inputCls} placeholder="e.g. United"
                    value={form.airline} onChange={(e) => update("airline", e.target.value)} />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Flight Number <span className="gold-text">*</span></Label>
                  <Input data-testid="input-flight-number" className={inputCls} placeholder="e.g. UA 1234"
                    value={form.flight_number} onChange={(e) => update("flight_number", e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Scheduled Arrival Time <span className="gold-text">*</span></Label>
                <Select value={form.arrival_time} onValueChange={(v) => update("arrival_time", v)}>
                  <SelectTrigger data-testid="input-arrival-time" className={`${inputCls} h-10`}>
                    <span className="flex items-center gap-2">
                      <Clock size={15} className="gold-text" />
                      <SelectValue placeholder="Select arrival time" />
                    </span>
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                    {ARRIVAL_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Number of Bags</Label>
              <Select value={form.num_bags} onValueChange={(v) => update("num_bags", v)}>
                <SelectTrigger data-testid="input-num-bags" className={`${inputCls} h-10`}><SelectValue placeholder="0" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {BAG_COUNTS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Ski Bags</Label>
              <Select value={form.ski_bags} onValueChange={(v) => update("ski_bags", v)}>
                <SelectTrigger data-testid="input-ski-bags" className={`${inputCls} h-10`}><SelectValue placeholder="0" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {BAG_COUNTS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Child Seats</Label>
              <Select value={form.child_seats} onValueChange={(v) => update("child_seats", v)}>
                <SelectTrigger data-testid="input-child-seats" className={`${inputCls} h-10`}><SelectValue placeholder="0" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {RANGE_3.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Bike Racks</Label>
              <Select value={form.bike_racks} onValueChange={(v) => update("bike_racks", v)}>
                <SelectTrigger data-testid="input-bike-racks" className={`${inputCls} h-10`}><SelectValue placeholder="0" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {RANGE_4.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">E-Bikes</Label>
              <Select value={form.e_bikes} onValueChange={(v) => update("e_bikes", v)}>
                <SelectTrigger data-testid="input-e-bikes" className={`${inputCls} h-10`}><SelectValue placeholder="0" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-white/15 text-white max-h-60">
                  {RANGE_4.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Special Requests</Label>
            <Textarea data-testid="input-notes" className={inputCls} rows={2} placeholder="Anything else we should know…"
              value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>

          <div className="pt-2 border-t border-white/10">
            <p className="text-xs tracking-label uppercase text-zinc-500 mt-3 mb-3">Your Details</p>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Full Name</Label>
                <Input data-testid="input-contact-name" className={inputCls} placeholder="James Whitmore"
                  value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Phone</Label>
                  <Input data-testid="input-contact-phone" className={inputCls} placeholder="+1 555 000 0000"
                    value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Email <span className="text-zinc-600">(optional)</span></Label>
                  <Input data-testid="input-contact-email" type="email"
                    className={`${inputCls} ${emailInvalid ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    placeholder="you@example.com"
                    value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} />
                  {emailInvalid && (
                    <p data-testid="email-error" className="text-red-400 text-sm mt-1.5 font-medium">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || emailInvalid}
            data-testid="submit-booking-btn"
            className="flex items-center justify-center w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
          >
            {submitting ? "Submitting…" : "Request a Quote"}
          </button>
          <Link
            to="/policies"
            data-testid="quote-view-policies-btn"
            className="flex items-center justify-center w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
          >
            View Our Policies
          </Link>
          <ContactButtons className="pt-2" />
        </form>
      )}
    </div>
  );
}
